"use client";

import { forwardRef } from "react";

import type { InputProps } from "@/components/types";
import type { JSX } from "react";

/**
 * @function Input
 * @description A reusable input component that supports both controlled and uncontrolled modes.
 * If a `value` prop is provided, it acts as a controlled component.
 * If a `defaultValue` prop is provided, it acts as an uncontrolled component.
 *
 * @param {InputProps} props - The props for the Input component.
 * @returns {JSX.Element} The rendered input component.
 *
 * @example
 * ```typescript
 * // Controlled input
 * <Input
 *   placeholder="Search flights..."
 *   value={searchTerm}
 *   onChange={(e) => setSearchTerm(e.target.value)}
 * />
 *
 * // Uncontrolled input with default value
 * <Input
 */
const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ name, ...props }, ref): JSX.Element => (
    <input ref={ref} name={name ?? props.id} {...props} />
  )
);

Input.displayName = "Input";

export default Input;
